"use client"

import * as React from "react"
import { Quote, Star } from 'lucide-react'
import {
  UICard,
  UICardContent,
  UICardFooter,
  UICardHeader,
} from './ui-card'
import { UITypography } from '../display/ui-typography'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'

export interface UICardTestimonialProps
  extends Omit<React.ComponentProps<typeof UICard>, 'children'> {
  testimonial: string
  author: string
  position?: string
  company?: string
  avatarUrl?: string
  avatarAlt?: string
  rating?: number
  showQuote?: boolean
  variant?: 'default' | 'accent' | 'minimal'
  children?: React.ReactNode
}

/**
 * Enhanced Testimonial Card Component
 * Perfect for showcasing customer reviews, testimonials, and social proof
 * Based on shadcn/ui Testimonial Card block with Seahorse branding
 */
export const UICardTestimonial = React.forwardRef<HTMLDivElement, UICardTestimonialProps>(
  ({
    testimonial,
    author,
    position,
    company,
    avatarUrl,
    avatarAlt,
    rating,
    showQuote = true,
    variant = 'default',
    className,
    children,
    ...props
  }, ref) => {
    return (
      <UICard
        ref={ref}
        className={cn(
          "relative overflow-hidden",
          {
            "border-l-4 border-l-accent bg-accent/5 dark:bg-accent/10": variant === 'accent',
            "shadow-none border-none bg-transparent": variant === 'minimal',
          },
          className
        )}
        {...props}
      >
        {/* Quote icon */}
        {showQuote && variant !== 'minimal' && (
          <UIBox className="absolute right-4 top-4 opacity-20">
            <Quote className="h-8 w-8 text-accent" />
          </UIBox>
        )}

        {/* Testimonial content */}
        <UICardHeader className="pb-4">
          <blockquote className="relative">
            {showQuote && variant === 'minimal' && (
              <Quote className="mb-2 h-5 w-5 text-accent opacity-60" />
            )}
<UITypography
              variant="p"
              size="default"
              className={cn(
                "leading-relaxed italic relative z-10",
                variant === 'minimal' ? "text-foreground" : "text-muted-foreground"
              )}
            >
              "{testimonial}"
            </UITypography>
          </blockquote>
        </UICardHeader>

        {/* Rating */}
        {rating && (
          <UICardContent className="py-2">
            <UIBox className="flex items-center gap-1">
              {Array.from({ length: 5 }, (_, i) => (
                <Star
                  key={i}
                  className={cn(
                    "h-4 w-4",
                    i < rating
                      ? "fill-accent text-accent"
                      : "text-muted-foreground/30"
                  )}
                />
              ))}
<UITypography as="span" variant="p" size="sm" color="muted" className="ml-2">
                {rating}/5
              </UITypography>
            </UIBox>
          </UICardContent>
        )}

        {/* Author information */}
        <UICardFooter className={cn(
          "pt-4",
          variant !== 'minimal' && "border-t border-border/50"
        )}>
          <UIBox className="flex items-center gap-3 w-full">
            {/* Avatar */}
            <UIBox className="flex-shrink-0">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={avatarAlt || `${author} avatar`}
                  className="h-10 w-10 rounded-full object-cover border border-border"
                />
              ) : (
                <UIBox className="h-10 w-10 rounded-full bg-accent/10 dark:bg-accent/20 flex items-center justify-center">
<UITypography as="span" variant="p" size="sm" weight="medium" className="text-accent">
                    {author.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                  </UITypography>
                </UIBox>
              )}
            </UIBox>

            {/* Author details */}
            <UIBox className="min-w-0 flex-1">
<UITypography variant="p" size="sm" weight="semibold" color="default" className="leading-none">
                {author}
              </UITypography>
              {(position || company) && (
<UITypography variant="p" size="xs" color="muted" className="mt-1">
                  {position && company ? `${position} at ${company}` : position || company}
                </UITypography>
              )}
            </UIBox>

            {/* Brand accent for accent variant */}
            {variant === 'accent' && (
              <UIBox className="flex-shrink-0">
                <UIBox className="h-2 w-2 rounded-full bg-accent"></UIBox>
              </UIBox>
            )}
          </UIBox>
        </UICardFooter>

        {children}
      </UICard>
    )
  }
)

UICardTestimonial.displayName = "UICardTestimonial"